/**
 * /v3 — single source of copy for both draft variations (`/v3/a` and
 * `/v3/b`). Every string a visitor sees on either page should trace back to
 * this file — edit copy once here, both variations update.
 *
 * All copy below is FINAL, approved strategy output (see
 * .handoffs/plan-v3-templates.md). Do not rewrite or add sentences here;
 * only the two page clients may adapt *structure* (JSX/CSS), never wording.
 *
 * Emphasis convention: `**bold text**` inside a string marks a span that
 * should render with emphasis (bold/color treatment) — parsed by the
 * `renderInline` helper in `./shared.tsx`. This mirrors how the /v2 source
 * used `<b>`/`<em>` for the same spans (v2's `<em>` renders as bold-weight +
 * color, not italic, so one marker covers both cases here).
 */

export const CALENDLY_URL = "https://calendly.com/jay-kreayt";
export const PHONE_TEL = "tel:+14472244665";
export const PHONE_DISPLAY = "+1 (447) 224-4665";
export const FOOTER_TEXT = "Kreayt · jay@kreayt.ai · kreayt.ai";

export type ActId = "I" | "II" | "III" | "IV";

// Plain-word rail labels only (Variation B's sticky-rail index). Variation A
// no longer shows act transition banners at all — the numbered "ACT II /
// ACT III / ACT IV" banners confused Jay in review and were removed
// entirely; the italic-gold narrator lines carry that storytelling job
// instead. `id` stays as the internal anchor/scrollspy key (DOM ids only,
// never displayed).
export const ACTS: { id: ActId; railLabel: string }[] = [
  { id: "I", railLabel: "The product" },
  { id: "II", railLabel: "The proof" },
  { id: "III", railLabel: "The value" },
  { id: "IV", railLabel: "Start" },
];

// ---------------------------------------------------------------------------
// ACT I — Drawer 01 · Hero
// ---------------------------------------------------------------------------
export const HERO = {
  eyebrow: "The askable portfolio · white-label for wealth firms",
  tag: "Ask your portfolio.",
  h1: "Give every client the “walk me through it” conversation — across your whole book.",
  core:
    "Kreayt puts an accountable, verifiable, interactive version of your firm in front of every client — without your advisors’ hours, and without breaking the supervision chain.",
  whiteLabel: "Your firm’s name on every video. Your clients never see us.",
  guardrail:
    "**It only tells your clients what the numbers say.** The moment a question needs judgment, it stops and hands it to you. It cannot give advice — by design.",
  ctaPrimary: { label: "Book a demo →", href: CALENDLY_URL },
  // RETIRED from the homepage (Round 7): it scrolled to a product that now
  // sits above the fold, so it pointed at something already on screen. Kept
  // only because the unlisted /v3/b reference draft still renders it.
  ctaSecondary: {
    label: "Watch Rachel’s review — then ask her portfolio",
    href: "#demo-a",
  },
};

// ---------------------------------------------------------------------------
// Round 7 — the fold rebalance (Jay's call, see docs/design/kreayt-hero-options.html)
//
// The homepage hero used to run ~783px before the product row started, so on a
// 682px-tall laptop viewport a visitor saw ZERO of the product without
// scrolling. Three blocks moved out of the hero to fix that. Nothing here is
// new wording — every string below is the same one that used to sit above the
// product, re-pointed at its new home, which is why these read as aliases.
//
//   TOPBAR  — the category line and the demo button now live in the top bar,
//             which previously held only the wordmark and ~1,010px of dead
//             space. Worth 117px of hero height for 12px of bar growth.
//   PROMISE — the explainer, the white-label line and the compliance guardrail
//             now sit directly UNDER the product row. They read better there:
//             NARRATOR_2 already opens the next section with "You just watched
//             it work. Here's why you can trust it."
//
// Result: the product row starts at 270px and lands whole above the fold with
// the video at its existing size. /v3/b is untouched and still renders the old
// arrangement from HERO directly.
// ---------------------------------------------------------------------------
export const TOPBAR = {
  categoryLine: HERO.eyebrow,
  cta: HERO.ctaPrimary,
  // Nav label only (not copy): the notes/blog at /blog — every note is a
  // video + podcast + piece generated the way client reviews are. Added
  // 2026-08-26; before this the blog was reachable only from LinkedIn.
  notes: { label: "Notes", href: "/blog" },
};

export const PROMISE = {
  core: HERO.core,
  whiteLabel: HERO.whiteLabel,
  guardrail: HERO.guardrail,
};

// Suggested-question chips inside the showcase row's "Ask it" pane (see
// ShowcaseRowV3.tsx). The standalone "Ask her portfolio" panel and the
// ElevenLabs voice widget it docked were removed per Jay's review — the
// chips (click-to-copy, no live typing/voice) are the only ask surface
// left on the page now.
export const ASK_CHIPS = {
  label: "Try asking:",
  suggestedQuestions: [
    "What did this video report?",
    "Why do we still own Blackstone?",
    "Should we trim the Apple?",
  ],
  simNote: "Simulated data · not investment advice.",
};

// Phone promotion band — verbatim from /v2.
export const PHONE_BAND = {
  heading: "Her portfolio also picks up the phone.",
  sub:
    "The analyst line, always on. Demo access code **4217** — in production, every client’s access code is their key.",
  simNote: "Simulated data · not investment advice.",
};

// Narrator interstitials — one before each act's content.
export const NARRATOR_1 =
  "However your client asks, the same verified numbers answer. Try all four doors —";
export const NARRATOR_2 =
  "You just watched it work. Here’s why you can trust it —";
export const NARRATOR_3 =
  "So it’s real, and it’s safe. Here’s what it’s worth to your firm —";

// "What every client gets" — three cards. Placement is a per-variation
// choice (spec's one allowed structural freedom); both variations currently
// place this between Act II and Act III.
export const THREE_CARDS = {
  label: "What every client gets — a short video that is",
  cards: [
    {
      title: "Personal",
      body:
        "Built from **this client’s** actual positions — the Tesla they bought in October and what drove it, not “the tech sector did well.”",
      premium:
        "**Premium:** your own face and voice presenting — with signed consent and AI-disclosure labeling.",
    },
    {
      title: "Verifiable",
      body:
        "Every number is **computed from cited market data** by a deterministic engine — never written by the AI. If it can’t be computed, it’s flagged, never faked.",
    },
    {
      title: "Interactive",
      body:
        "The client asks **“why is my energy position down?”** — and the answer is recomputed and redrawn from the same data. A conversation, not a document.",
    },
  ],
};

// ---------------------------------------------------------------------------
// ACT II — Drawer 05 · Compliance
// ---------------------------------------------------------------------------
export const COMPLIANCE = {
  lead:
    "Compliance-ready by design — deterministic data, full audit trail, advisor sign-off.",
  checklist: [
    { text: "Advisor approval before first use — who, when, and edits logged" },
    {
      text:
        "Computed from cited sources — macro series direct from the Federal Reserve (FRED), prices and fundamentals from licensed market-data feeds; every figure traceable to its source, never model-generated",
    },
    { text: "Full-provenance export for your books-and-records archive" },
    { text: "SOC 2 Type II — planned; audit begins with production rollout.", wip: true },
  ],
  liability: {
    question: "Who’s liable if a number is wrong?",
    answer:
      "The numbers aren’t written by AI — they’re computed by a deterministic engine from cited market data: macro series direct from the Federal Reserve (FRED), prices and fundamentals from licensed market-data feeds, every figure traceable to its source. If a figure can’t be computed from source data, it’s flagged and left out, never approximated. And nothing reaches a client until your advisor signs off.",
  },
  note: "Compliance-ready, not “FINRA-approved” — your advisor owns the sign-off.",
  briefLine:
    "A written compliance brief for your CCO is available on request.",
  chain: [
    { label: "Market data (licensed feeds · Federal Reserve)" },
    { label: "Computed" },
    { label: "Your sign-off", ok: true },
    { label: "Client" },
  ],
};

// ---------------------------------------------------------------------------
// ACT III — Drawer 06 · Capacity
// ---------------------------------------------------------------------------
export const CAPACITY = {
  body:
    "Five advisors. Seven hundred fifty conversations owed every quarter. It physically can’t happen by hand — so most clients get a PDF they don’t open. And the generation inheriting the money engages on its own time, in formats it will actually watch.",
  citation: "(Javelin Survey of Retail Investors, 2022)",
};

// ---------------------------------------------------------------------------
// Drawer 07 · Calculator
// ---------------------------------------------------------------------------
export const CALCULATOR = {
  label: "Run your own math",
  h2: "What does review season already cost you?",
  sub: "Four inputs — yours, not ours. The page computes; you decide. We assert nothing.",
  editHint:
    "✎ Your numbers — click any figure and type. Everything below recomputes.",
  inputs: {
    advisors: { label: "Advisors", defaultValue: 5 },
    clientsPerAdvisor: { label: "Clients per advisor", defaultValue: 150 },
    prepMinutes: {
      label: "Reporting & communication minutes per client, per quarter",
      defaultValue: 150,
    },
    hourlyRate: {
      label: "An advisor-hour is worth $",
      defaultValue: 200,
    },
  },
  // Broadened time input (Round 6): a caption, tap-to-fill presets, and a
  // build-your-own-estimate disclosure. The disclosure body is a set of
  // PROMPTS the visitor counts for themselves — deliberately not industry
  // statistics.
  prepCaption: "prep · meeting · follow-up · ad-hoc questions",
  prepPresets: [
    { label: "High-touch 4h+", value: 240 },
    { label: "Typical 2.5h", value: 150 },
    { label: "Light-touch 1h", value: 60 },
  ],
  prepDisclosure: {
    summary: "What’s in this number?",
    body:
      "Count what applies to you, per client, per quarter: review prep (pulling performance, what moved, talking points) · the meeting itself, plus scheduling · follow-up notes and the “as discussed” email · ad-hoc questions between meetings.",
  },
  // Round 7 — cost of doing nothing. The calculator used to end on a
  // "by hand vs Kreayt at $100 a client" comparison. Price is off the
  // homepage entirely now (it lives on /pilot), so the two-sided compare,
  // the ratio line and the pilot anchor are gone. What is left is the half
  // that was never ours to assert: what covering the book by hand already
  // costs the firm, computed from the visitor's own four inputs.
  perClient: {
    handLabel: "Your advisor’s time — per client, per quarter",
  },
  firmLead: "Across your whole book, every quarter",
  firmTotalLabel: "What that time is worth, every quarter",
  outputs: {
    conversations: "Conversations owed each quarter",
    advisorDays: "Advisor-days that would consume",
  },
  finePrint:
    "Arithmetic only — no benchmarks, no industry averages. Your numbers, your conclusion.",
};

export const CALCULATOR_VERDICT =
  "That is what covering your whole book by hand costs every quarter — and today, most of those conversations simply don’t happen. The cost recurs either way; it’s just currently paid in advisor hours and unopened PDFs.";

export const CALCULATOR_VERDICT_DEGENERATE =
  "Enter your firm’s numbers above — the page computes, you decide.";

// ---------------------------------------------------------------------------
// Drawer 08 · Price (two variants — see ?pricing=2 switch)
//
// RETIRED FROM THE HOMEPAGE (Round 7, Jay's call): price is off the landing
// page entirely and lives on the unlisted one-pager at /pilot, which carries
// its own copy. The first pilots are free, so there is no number to put in
// front of a first-time visitor. These exports survive ONLY because the
// unlisted /v3/b reference draft still renders them — do not reintroduce them
// to /v3/a or app/page.tsx.
// ---------------------------------------------------------------------------
export const PRICE_INCLUDED_BOX = {
  title: "What “covered” includes:",
  text:
    "the client’s quarterly review video, one on-demand video, 30 minutes of live Q&A, and the audio edition — every quarter.",
};

export const PRICE_VARIANT_1 = {
  label: "What it costs",
  h2: "One number, said out loud.",
  body:
    "The pilot is **$2,500** — twenty-five clients, one full quarter, **$100 a client**. That’s the entire price. Production keeps the same unit — per client, per quarter — and the rate comes down as coverage grows across your book. We set it together when your pilot numbers are in.",
};

export const PRICE_VARIANT_2 = {
  label: "What it costs",
  h2: "The rate falls as coverage grows.",
  body:
    "Same unit, said out loud — **$2,500 = 25 clients × $100** — then the shape of what comes after:",
  tiers: [
    { range: "Pilot — 25 clients, 90 days", rate: "$100" },
    { range: "First 250 clients", rate: "$85" },
    { range: "Whole book beyond 250", rate: "from $60" },
  ],
  caveat: "Production rates illustrative — fixed together at pilot conversion",
};

// ---------------------------------------------------------------------------
// Drawer 08b · Ceiling
// ---------------------------------------------------------------------------
export const CEILING = {
  h3: "One review a quarter. One more when it matters. Nothing unsupervised.",
  checklist: [
    {
      status: "check" as const,
      text:
        "**One quarterly review video** per covered client — the quarter is the product’s rhythm, and the review is its delivery.",
    },
    {
      status: "check" as const,
      text:
        "**One on-demand video** each quarter — a market-shock explainer, or a prospect walk-through before the second meeting.",
    },
    {
      status: "check" as const,
      text:
        "**Thirty minutes of voice and phone Q&A** — the analyst line, on the page or over the phone, per covered client each quarter.",
    },
    {
      status: "check" as const,
      text:
        "**The audio edition, included** — the same review as a private podcast episode, in the client’s own feed.",
    },
    {
      status: "check" as const,
      text:
        "**Re-renders during approval** — revising the video until the advisor signs off is part of making the one video right, not a separate charge.",
    },
  ],
  keyLine:
    "The ceiling isn’t a meter we bolted on. **No video — quarterly or on-demand — reaches a client without advisor sign-off.** The supervision chain you’re buying for compliance is the same gate that makes unsupervised generation impossible.",
};

// ---------------------------------------------------------------------------
// Drawer 09 · Why now
// ---------------------------------------------------------------------------
export const WHY_NOW = {
  label: "Why now",
  body:
    "The quarter is the clock. Every quarter uncovered is another cycle of unopened PDFs across your book — and the generation inheriting the money is already deciding which advisors it will keep. Be the firm whose updates the heirs actually watch — before the wealth transfer, not after.",
  capacityLine:
    "We onboard a small number of pilot firms each quarter — every pilot gets direct founder attention.",
};

// ---------------------------------------------------------------------------
// CLOSE — Drawer 10 · Pilot close (verbatim from /v2)
// ---------------------------------------------------------------------------
export const CLOSING_ONELINER =
  "Templated reporting talks at everyone in the model the same way. Generative AI invents numbers **you’re** liable for. An agency can’t cover a book. Kreayt is built to be all three at once: **personal, verifiable, interactive.**";

// Round 7 — the price sentence is out. Pricing left the homepage entirely and
// lives on /pilot; the first pilots are free, so there is no number to name
// here anyway. Everything else is the original wording.
export const PILOT_CLOSE = {
  heading: "Start with 25 clients.",
  sub:
    "A 90-day pilot, scoped to your book — and the first pilots are free. We measure the time you get back first, watch rates second. If the numbers aren’t there, we part ways.",
  cta: { label: "Book a demo →", href: CALENDLY_URL },
  detailLink: { label: "What a pilot covers →", href: "/pilot" },
};

// ---------------------------------------------------------------------------
// Drawer 11 · FAQ (cost + liability questions moved to drawers 08 / 05)
// ---------------------------------------------------------------------------
export const FAQ = {
  label: "Questions advisors — and their CCOs — ask",
  items: [
    {
      q: "Will it sound like AI slop to my clients?",
      a:
        "No script templates. Each video is built from that client’s actual positions and what moved them this quarter. On the premium tier, it’s your own face and voice presenting — with your signed consent and clear AI-disclosure labeling on every video.",
    },
    {
      q: "What does my compliance officer need to approve?",
      a:
        "Your advisor reviews and approves before first use — who approved, when, and what was edited is logged. Every video exports a full provenance trail for your books-and-records archive. There’s a written brief you can hand your CCO directly.",
    },
    {
      q: "How do you get my client data?",
      a:
        "CSV upload of positions to start — no custodian integration required for a pilot. On the phone line, every client authenticates with their own access code before any portfolio detail is spoken. We’re shaping direct integrations with pilot partners. A technical integration note is available on request.",
    },
  ],
};
